import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import { useAppContext } from "../../../../../AppContext";
import { IEventWizardInitialData } from "../../models/eventWizardInitialDataModel";
import { ISelectFieldModel } from "../../../../../common/components/forms/inputs/dropdown/models/selectFieldModel";
import { PageMode } from "../../../../../common/types";
import { CUSTOM_FORM_LINK_OPTION_ID } from "../../../../../common/preReservationForms/constants";
import { useParams } from "react-router";
import {
  ALL_PLAN_TYPES_SENTINEL,
  eventWizardFormDefaultValues,
  eventWizardFormSchema,
  IClassWizardFormModel,
  NONE_PLAN_TYPES_SENTINEL,
} from "../models/classWizardFormModel";
import {
  EventDurationMetric,
  EventType,
  SubscriptionPaymentMode,
} from "../../../../../common/enums";
import { useFormValidationMessages } from "../../../../../common/infrastructure/hooks/useFormHelper";
import { getDuplicateVisitorIds } from "../utils/classWizardRecurringVisitorsUtility";

export const useClassWizardForm = (
  selectedDate?: Date,
  studioInstructors?: ISelectFieldModel[],
  initialData?: IEventWizardInitialData,
  pageMode?: PageMode,
) => {
  const { selectedStudioId } = useAppContext();
  const { id } = useParams();

  const form = useForm<IClassWizardFormModel>({
    defaultValues: eventWizardFormDefaultValues,
    resolver: yupResolver(eventWizardFormSchema),
  });

  // populate default values
  useEffect(() => {
    if (
      pageMode !== "edit" &&
      studioInstructors &&
      studioInstructors.length > 0
    ) {
      form.setValue("employeeId", studioInstructors[0].id);
    }

    if (
      pageMode !== "edit" &&
      initialData?.allAvailableUsers &&
      initialData?.allAvailableUsers.length > 0
    ) {
      form.setValue("individualUserId", initialData?.allAvailableUsers[0].id);
    }

    if (initialData?.currentlySelectedEvent) {
      if (pageMode !== "duplicate") {
        form.setValue("id", Number(id));
      }
      // Only carry the cancellation status when editing the actual event.
      // On duplicate it must reset to the default ("will take place").
      if (pageMode === "edit") {
        form.setValue(
          "willTakePlace",
          !initialData?.currentlySelectedEvent.isCancelled,
        );
      }
      form.setValue(
        "employeeId",
        initialData?.currentlySelectedEvent.employeeId ?? null,
      );
      if (initialData?.currentlySelectedEvent.individualUserId) {
        form.setValue(
          "individualUserId",
          initialData?.currentlySelectedEvent.individualUserId ?? 0,
        );
      }
      form.setValue("name", initialData?.currentlySelectedEvent.name);
      form.setValue(
        "availableSpaces",
        initialData?.currentlySelectedEvent.availableSpaces,
      );
      form.setValue(
        "durationInMinutes",
        initialData?.currentlySelectedEvent.durationInMinutes,
      );
      form.setValue(
        "allowOnlineReservations",
        initialData?.currentlySelectedEvent.allowOnlineReservations,
      );
      form.setValue(
        "isRemote",
        initialData?.currentlySelectedEvent.isRemote ?? false,
      );
      form.setValue(
        "isMultisportAvailable",
        initialData?.currentlySelectedEvent.isMultisportAvailable,
      );
      const existingMultisportCustomPrice =
        initialData?.currentlySelectedEvent.multisportCustomPrice;
      form.setValue(
        "hasCustomMultisportPrice",
        existingMultisportCustomPrice != null,
      );
      form.setValue(
        "multisportCustomPrice",
        existingMultisportCustomPrice ?? null,
      );
      form.setValue("price", initialData?.currentlySelectedEvent.price);
      // Translate the stored payment mode back into the UI sentinels:
      //   None → [-1] ("Никакви"); Specific → the ids; All / empty → [0] ("Всички").
      const selectedEvent = initialData.currentlySelectedEvent;
      const planTypeIdsValue =
        selectedEvent.paymentMode === SubscriptionPaymentMode.None
          ? [NONE_PLAN_TYPES_SENTINEL]
          : selectedEvent.planTypeIds?.length > 0
            ? selectedEvent.planTypeIds
            : [ALL_PLAN_TYPES_SENTINEL];
      form.setValue("planTypeIds", planTypeIdsValue);
      form.setValue(
        "date",
        new Date(initialData?.currentlySelectedEvent.startTime),
      );
      form.setValue(
        "description",
        initialData?.currentlySelectedEvent.description,
      );
      form.setValue("type", initialData?.currentlySelectedEvent.type);
      form.setValue(
        "showInUserPortal",
        initialData?.currentlySelectedEvent.showInUserPortal,
      );
      form.setValue(
        "forceEmailNotifications",
        initialData?.currentlySelectedEvent.forceEmailNotifications,
      );
      form.setValue(
        "requirePrepayment",
        initialData?.currentlySelectedEvent.requirePrepayment ?? false,
      );
      form.setValue(
        "prepaymentProvider",
        initialData?.currentlySelectedEvent.prepaymentProvider ?? null,
      );
      form.setValue(
        "prepaymentLink",
        initialData?.currentlySelectedEvent.prepaymentLink,
      );
      form.setValue(
        "allowSubscriptionPrepayment",
        initialData?.currentlySelectedEvent.allowSubscriptionPrepayment ??
          false,
      );
      form.setValue(
        "requireFormCompletion",
        initialData?.currentlySelectedEvent.requireFormCompletion ?? false,
      );
      const existingFormId =
        initialData?.currentlySelectedEvent.preReservationFormId;
      const existingFormLink =
        initialData?.currentlySelectedEvent.formCompletionLink;
      form.setValue(
        "preReservationFormId",
        existingFormId ??
          (initialData?.currentlySelectedEvent.requireFormCompletion &&
          existingFormLink
            ? CUSTOM_FORM_LINK_OPTION_ID
            : null),
      );
      form.setValue(
        "formCompletionLink",
        initialData?.currentlySelectedEvent.formCompletionLink,
      );
      form.setValue(
        "allowMusicSuggestions",
        initialData?.currentlySelectedEvent.allowMusicSuggestions ?? false,
      );
      form.setValue("hallId", initialData?.currentlySelectedEvent.hallId);
      form.setValue(
        "eventTypeCategoryId",
        initialData?.currentlySelectedEvent.eventTypeCategoryId,
      );
      // A duplicated group class starts with the clients of the copied occurrence as its
      // recurring visitors; staff can still remove or add people before saving.
      if (
        pageMode === "duplicate" &&
        selectedEvent.type !== EventType.Individual
      ) {
        const visitorIds = getDuplicateVisitorIds(
          selectedEvent.attendeeUserIds,
          initialData.allAvailableUsers,
        );
        form.setValue("recurringVisitorsIds", visitorIds);
        form.setValue("hasRecurringVisitors", visitorIds.length > 0);
      }
    } else if (initialData?.studioSettings) {
      form.setValue(
        "availableSpaces",
        initialData?.studioSettings.eventBaseFreeSpaces,
      );
      form.setValue(
        "isMultisportAvailable",
        initialData?.studioSettings.isMultisportAvailable,
      );

      form.setValue("price", initialData?.studioSettings.eventBasePrice ?? 0);
      form.setValue("durationInMinutes", EventDurationMetric.FortyFiveMin);

      if (!!selectedDate) {
        form.setValue("date", selectedDate);
      }
    }

    // this is needed
    if (selectedStudioId) {
      form.setValue("studioId", selectedStudioId);
    }
  }, [selectedStudioId, selectedDate, initialData, studioInstructors]);

  useFormValidationMessages<IClassWizardFormModel>(form);

  return form;
};
